
import { computed, defineComponent, onBeforeMount, reactive } from 'vue'
import { useRouter } from 'vue-router'

// Components
import TheButton from '@/common/components/UI/TheButton/index.vue'
import TheInput from '@/common/components/UI/TheInput/index.vue'
import TheSelect from '@/common/components/UI/TheSelect/index.vue'
import Checkbox from '@/common/components/UI/Checkbox/index.vue'

// Classes
import { DataState } from '@/core/classes/DataState.class'

// Types
import { CompanyT } from '@/core/types/companies.types'
import { TmcReceiptItem, TmcT, TmcCreateAdmissionPayload } from '@/core/types/tmc.types'
import { WarehouseT } from '@/core/types/warehouses.types'

// Realisations
import { companies } from '@/core/realisations/companies.realisation'
import { tmc } from '@/core/realisations/tmc.realisation'
import { warehouses } from '@/core/realisations/warehouses.realisation'

// Validation
import useVuelidate from '@vuelidate/core'
import { required, integer, decimal } from '@vuelidate/validators'

// Composable
import useHelpers from '@/common/composable/useHelpers'
import useNotifications from '@/common/composable/useNotifications'
import { useRound } from '@/common/composable/useRound'

export default defineComponent({
    components: {
        'the-button': TheButton,
        'the-input': TheInput,
        'the-select': TheSelect,
        checkbox: Checkbox,
    },
    setup() {
        const router = useRouter()
        const { findItemIndexById } = useHelpers()
        const notifications = useNotifications()
        const { roundTo } = useRound()

        const state = reactive({
            companyId: null as number | null,
            jiraNumber: null as string | null,
            warehouseId: null as number | null,
            warrantNumber: null as string | null,
            //
            items: [] as {
                tmc: number
                tmc_name: string
                amount: number
                unitPrice: number
                isNds: boolean
            }[],
            //
            is_loading: false,
        })

        const data_state = reactive({
            companies: new DataState<CompanyT>({}),
            warehouses: new DataState<WarehouseT>({}),
            tmc: new DataState<TmcT>({}),
        })

        const item_state = reactive({
            tmc: null as number | null,
            amount: null as string | null,
            unitPrice: null as string | null,
            isNds: false,
        })

        const itemCost = computed(() => {
            if (!item_state.amount) return 0
            if (!item_state.unitPrice) return 0

            const numered_amount = Number(item_state.amount)
            const numered_price = Number(item_state.unitPrice)

            if (isNaN(numered_amount) || isNaN(numered_price)) return 0

            return numered_amount * numered_price
        })

        const totalItemsCost = computed(() => {
            let total_cost = 0
            if (!state.items.length) return total_cost

            const item_costs = state.items.map((item) => item.amount * item.unitPrice)
            return item_costs.reduce((accumulator, currentValue) => accumulator + currentValue)
        })

        const state_validation = useVuelidate(
            {
                companyId: { required },
                jiraNumber: { required },
                warehouseId: { required },
                warrantNumber: { required },
                items: { required },
            },
            state
        )

        const item_validation = useVuelidate(
            {
                tmc: { required },
                amount: { required, decimal },
                unitPrice: { required, decimal },
            },
            item_state
        )

        // Companies
        const loadCompanies = async (page: number, search?: string) => {
            data_state.companies.pagination.current_page = page

            try {
                data_state.companies.is_loading = true
                const response = await companies.getListBilling({
                    getAll: 1,
                    limit: 9999,
                    search,
                    isExtended: 0,
                })
                data_state.companies.list = response.data

                data_state.companies.pagination.total_pages = response.meta.last_page

                data_state.companies.is_failed = false
                data_state.companies.failure.error_state.is_cancel = false
            } catch (error) {
                data_state.companies.failure.catchError(error)

                if (!data_state.companies.failure.error_state.is_cancel) {
                    data_state.companies.is_failed = true
                }
            } finally {
                if (!data_state.companies.failure.error_state.is_cancel) {
                    data_state.companies.is_loading = false
                }
            }
        }

        const loadMoreCompanies = async (page: number) => {
            if (page > data_state.companies.pagination.total_pages) return
            data_state.companies.pagination.current_page = page

            try {
                data_state.companies.is_loading = true

                const response = await companies.getListBilling({
                    getAll: 1,
                    limit: 9999,
                    isExtended: 0,
                })
                data_state.companies.list = data_state.companies.list.concat(response.data)

                data_state.companies.pagination.total_pages = response.meta.last_page

                data_state.companies.is_failed = false
                data_state.companies.failure.error_state.is_cancel = false
            } catch (error) {
                data_state.companies.failure.catchError(error)

                if (!data_state.companies.failure.error_state.is_cancel) {
                    data_state.companies.is_failed = true
                }
            } finally {
                if (!data_state.companies.failure.error_state.is_cancel) {
                    data_state.companies.is_loading = false
                }
            }
        }

        const searchCompanies = (search_query: string) => {
            // data_state.companies.pagination.current_page
            loadCompanies(1, search_query)
        }

        const unshiftFindedCompany = async (company: CompanyT) => {
            await loadCompanies(data_state.companies.pagination.current_page)
            data_state.companies.list.unshift(company)
        }

        // TMC
        const loadTMC = async (page: number, search?: string) => {
            data_state.tmc.pagination.current_page = page

            try {
                data_state.tmc.is_loading = true
                const response = await tmc.getList(page, search)
                data_state.tmc.list = response.data

                data_state.tmc.pagination.total_pages = response.meta.last_page

                data_state.tmc.is_failed = false
                data_state.tmc.failure.error_state.is_cancel = false
            } catch (error) {
                data_state.tmc.failure.catchError(error)

                if (!data_state.tmc.failure.error_state.is_cancel) {
                    data_state.tmc.is_failed = true
                }
            } finally {
                if (!data_state.tmc.failure.error_state.is_cancel) {
                    data_state.tmc.is_loading = false
                }
            }
        }

        const loadMoreTMC = async (page: number) => {
            if (page > data_state.tmc.pagination.total_pages) return
            data_state.tmc.pagination.current_page = page

            try {
                data_state.tmc.is_loading = true

                const response = await tmc.getList(page)
                data_state.tmc.list = data_state.tmc.list.concat(response.data)

                data_state.tmc.pagination.total_pages = response.meta.last_page

                data_state.tmc.is_failed = false
                data_state.tmc.failure.error_state.is_cancel = false
            } catch (error) {
                data_state.tmc.failure.catchError(error)

                if (!data_state.tmc.failure.error_state.is_cancel) {
                    data_state.tmc.is_failed = true
                }
            } finally {
                if (!data_state.tmc.failure.error_state.is_cancel) {
                    data_state.tmc.is_loading = false
                }
            }
        }

        const searchTMC = (search_query: string) => {
            loadTMC(1, search_query)
        }

        const unshiftFindedTMC = async (tmc: TmcT) => {
            await loadTMC(data_state.tmc.pagination.current_page)
            data_state.tmc.list.unshift(tmc)
        }

        // Warehouses
        const loadWarehouses = async () => {
            data_state.warehouses.pagination.current_page = 1

            try {
                data_state.warehouses.is_loading = true
                const response = await warehouses.getWarehouses()
                data_state.warehouses.list = response.data

                // data_state.warehouses.pagination.total_pages = response.meta.last_page

                data_state.warehouses.is_failed = false
                data_state.warehouses.failure.error_state.is_cancel = false
            } catch (error) {
                data_state.warehouses.failure.catchError(error)

                if (!data_state.warehouses.failure.error_state.is_cancel) {
                    data_state.warehouses.is_failed = true
                }
            } finally {
                if (!data_state.warehouses.failure.error_state.is_cancel) {
                    data_state.warehouses.is_loading = false
                }
            }
        }

        onBeforeMount(() => {
            loadCompanies(1)
            loadTMC(1)
            loadWarehouses()
        })

        const resetItemForm = () => {
            item_state.tmc = null
            item_state.amount = null
            item_state.unitPrice = null
            item_state.isNds = false

            item_validation.value.$reset()
        }

        const addItem = () => {
            item_validation.value.$touch()
            if (item_validation.value.$invalid) return

            const index = findItemIndexById(item_state.tmc as number, data_state.tmc.list)
            if (index < 0) return

            const tmc_name = data_state.tmc.list[index].name
            const maybe_selected_tmc = state.items.find((item) => item.tmc === item_state.tmc)

            if (maybe_selected_tmc) {
                notifications.notify({
                    title: `ТМЦ '${tmc_name}' уже выбран`,
                    description: 'Пожалуйста, выберите другой ТМЦ',
                    type: 'danger',
                })
                return
            }

            const item = {
                tmc: item_state.tmc as number,
                tmc_name,
                amount: Number(item_state.amount as string),
                unitPrice: Number(item_state.unitPrice as string),
                isNds: item_state.isNds,
            }

            state.items.push(item)
            resetItemForm()
        }

        const removeItem = (item_index: number) => {
            state.items.splice(item_index, 1)
        }

        const mapItems = (item: {
            tmc: number
            tmc_name: string
            amount: number
            unitPrice: number
            isNds: boolean
        }): TmcReceiptItem => {
            return {
                inventoryItemId: item.tmc,
                amount: item.amount,
                unitPrice: item.unitPrice,
                isNds: item.isNds,
            }
        }

        const submitForm = async () => {
            if (state.is_loading) return

            if (
                state.items.length === 0 ||
                (state.items.length > 0 &&
                    (item_state.tmc || item_state.amount || item_state.unitPrice))
            ) {
                addItem()
            }

            state_validation.value.$touch()

            if (state_validation.value.$invalid) {
                if (state.items.length === 0) {
                    notifications.notify({
                        title: 'Ошибка валидации',
                        description: 'Для создания заявки необходимо добавить хотя бы 1 товар',
                        type: 'danger',
                    })
                }

                return
            }

            try {
                state.is_loading = true

                const payload: TmcCreateAdmissionPayload = {
                    companyId: state.companyId as number,
                    jiraNumber: state.jiraNumber as string,
                    warehouseId: state.warehouseId as number,
                    warrantNumber: state.warrantNumber as string,
                    receiptItems: state.items.map(mapItems),
                }

                await tmc.createAdmission(payload)

                notifications.notify({ title: 'Заявка на поступление создана', type: 'success' })
                router.push('/tmc/admission')
            } catch (error) {
                console.error(error)
                notifications.notifyError({
                    title: 'При создании поступления произошла ошибка',
                    error,
                })
            } finally {
                state.is_loading = false
            }
        }

        return {
            state,
            data_state,
            item_state,
            itemCost,
            totalItemsCost,
            //
            state_validation,
            item_validation,
            //
            loadCompanies,
            loadMoreCompanies,
            searchCompanies,
            unshiftFindedCompany,
            loadTMC,
            loadMoreTMC,
            searchTMC,
            unshiftFindedTMC,
            loadWarehouses,
            //
            addItem,
            removeItem,
            submitForm,
            //
            roundTo,
        }
    },
})
